/* Migrated Essential cart presentation/gift behavior. The V3 singleton prevents double initialization during migration. */
(function () {
  'use strict';

  const KEY = '__debacqCartCompleteV3';
  if (window[KEY]?.version === 9) {
    window[KEY].refresh();
    return;
  }
  // Replace a previously injected app helper instead of silently keeping its old hooks.
  window[KEY]?.destroy?.();
  // Retire the earlier helper instead of running two implementations.
  if (window.__debacqGiftUIV1 && window.__debacqGiftUIV1.destroy) {
    window.__debacqGiftUIV1.destroy();
  }

  const E = name => '[data-essential-cart-element="' + name + '"]';
  const MODAL = E('choose-gift-modal');
  const ROW = E('choose-gift-row');
  const ITEM = E('cart-drawer-item');
  const CART_ITEMS = E('cart-drawer-item-list') + ' ' + ITEM;
  const CHOOSE = /^choose\s+(?:(?:your|a|free)\s+){0,2}gifts?[.!]?$/i;
  const STORAGE = 'debacq:gift-modal:confirmed:v3';
  const OPEN_DELAY = 450;
  const RETRY_DELAY = 1400;
  const LOADING_TIMEOUT = 30000;
  const RESET_DELAY = 1800;
  const BUY_QUANTITY = 2; // Used only to reset this Buy 2 offer's prompt.

  let confirmed = false;
  let stopped = false;
  let frame = 0;
  let openTimer = 0;
  let resetTimer = 0;
  let pendingTimeout = 0;
  let retryTimer = 0;
  let themeLoading = false;
  let sawThemeBusy = false;
  let loadingTimer = 0;
  let noBusyTimer = 0;
  let loadingLayerTimer = 0;
  let loadingLayer = null;
  let nativeLoadingSequence = 0;
  let submittedForm = null;
  let submitObserver = null;
  let attempts = 0;
  let retryAfter = 0;
  let autoClick = false;
  let pending = null;
  let previousCart = '';
  let loaderTemplate = null;
  let loaderSignature = '';
  let status = null;
  const copiedIcons = new WeakMap();

  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE) || 'null');
    confirmed = !!saved && saved.seen === true && Date.now() - saved.at < 1800000;
  } catch (_) {}

  const text = value => String(value || '').normalize('NFKC')
    .replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/\s+/g, ' ').trim();

  function flag(node, name, enabled) {
    if (enabled) {
      if (node.getAttribute(name) !== 'true') node.setAttribute(name, 'true');
    } else if (node.hasAttribute(name)) {
      node.removeAttribute(name);
    }
  }

  function isZero(value) {
    value = text(value);
    if (/^free$/i.test(value)) return true;
    if (!value || /[%\u066a]/.test(value)) return false;
    const digits = value.match(/\p{Nd}/gu);
    return !!digits && digits.every(d => /^[0\u0660\u06f0\u0966]$/.test(d));
  }

  function visible(node) {
    if (!(node instanceof Element) || !node.isConnected) return false;
    const rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    for (let el = node; el instanceof Element; el = el.parentElement) {
      const style = getComputedStyle(el);
      if (el.hidden || style.display === 'none' ||
          style.visibility === 'hidden' || style.visibility === 'collapse' ||
          Number(style.opacity) === 0) return false;
    }
    return true;
  }

  function available(node) {
    if (!visible(node)) return false;
    if (node.closest('[inert], :disabled, [disabled], [aria-disabled="true"]')) return false;
    return getComputedStyle(node).pointerEvents !== 'none';
  }

  function drawerOpen() {
    const header = document.querySelector(E('cart-drawer-header'));
    if (!visible(header)) return false;
    const rect = header.getBoundingClientRect();
    return rect.right > 0 && rect.left < innerWidth &&
      rect.bottom > 0 && rect.top < innerHeight;
  }

  function openModal() {
    return [...document.querySelectorAll(MODAL)].find(visible) || null;
  }

  function chooseControl() {
    const root = document.getElementById('essential-cart-drawer-portal');
    if (!root) return null;
    const selector = E('reward-milestone-label') +
      ', button, [role="button"], a, [data-essential-cart-element*="choose"]';
    for (const node of root.querySelectorAll(selector)) {
      if (node.closest(MODAL)) continue;
      if (!CHOOSE.test(text(node.textContent)) &&
          !CHOOSE.test(text(node.getAttribute('aria-label')))) continue;
      const control = node.closest('button, [role="button"], a') || node;
      if (root.contains(control) && available(control)) return control;
    }
    return null;
  }

  function markPrices() {
    // Keep native text and cart values intact. Only add styling attributes.
    const nodes = new Set(document.querySelectorAll(CART_ITEMS));
    document.querySelectorAll(MODAL + ' ' + ITEM).forEach(node => nodes.add(node));
    nodes.forEach(item => {
      const price = item.querySelector(E('line-item-discounted-price')) ||
        item.querySelector(E('line-item-regular-price'));
      const free = !!price && isZero(price.textContent);
      flag(item, 'data-debacq-free-item', free);
      item.querySelectorAll('[data-debacq-free-price]').forEach(previous => {
        if (previous !== price || !free) previous.removeAttribute('data-debacq-free-price');
      });
      if (price) flag(price, 'data-debacq-free-price', free);
    });
  }

  function cartState() {
    const rows = [...document.querySelectorAll(CART_ITEMS)]
      .filter(item => !item.closest(MODAL));
    let paid = 0;
    let free = 0;
    const entries = rows.map(item => {
      const quantityNode = item.querySelector(E('quantity-value'));
      const quantity = Math.max(1, parseInt(quantityNode &&
        ('value' in quantityNode ? quantityNode.value : quantityNode.textContent), 10) || 1);
      const zero = item.getAttribute('data-debacq-free-item') === 'true';
      const title = text(item.querySelector(E('line-item-title'))?.textContent);
      const price = text(item.querySelector(E('line-item-price'))?.textContent);
      if (zero) free += quantity;
      else paid += quantity;
      return { title, quantity, zero, price };
    });
    const empty = [...document.querySelectorAll(E('cart-drawer-empty'))].some(visible);
    return { paid, free, entries, ready: rows.length > 0 || empty,
      signature: JSON.stringify(entries) };
  }

  function confirmShown() {
    if (confirmed) return;
    confirmed = true;
    try {
      sessionStorage.setItem(STORAGE, JSON.stringify({ seen: true, at: Date.now() }));
    } catch (_) {}
  }

  function cancelOpen() {
    clearTimeout(openTimer);
    openTimer = 0;
  }

  function cancelReset() {
    clearTimeout(resetTimer);
    resetTimer = 0;
  }

  function announce(message) {
    if (!status) {
      status = document.createElement('span');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      status.style.cssText = 'position:fixed;width:1px;height:1px;padding:0;' +
        'margin:-1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0;';
      document.body.appendChild(status);
    }
    status.textContent = message;
  }

  function cloneVisual(source, withAnimation) {
    const copy = source.cloneNode(true);
    const sources = [source, ...source.querySelectorAll('*')];
    const copies = [copy, ...copy.querySelectorAll('*')];
    const properties = ['color', 'fill', 'fill-opacity', 'fill-rule', 'stroke',
      'stroke-opacity', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
      'stroke-dasharray', 'stroke-dashoffset', 'vector-effect'];
    sources.forEach((node, index) => {
      const style = getComputedStyle(node);
      properties.forEach(property => copies[index].style.setProperty(
        property, style.getPropertyValue(property), 'important'
      ));
      if (withAnimation) {
        ['animation', 'width', 'height', 'border', 'border-radius', 'background']
          .forEach(property => copies[index].style.setProperty(
            property, style.getPropertyValue(property), 'important'
          ));
      }
    });
    copy.removeAttribute('id');
    copy.setAttribute('aria-hidden', 'true');
    if (copy.tagName.toLowerCase() === 'svg') copy.setAttribute('focusable', 'false');
    return copy;
  }

  function copyCloseIcons() {
    const source = document.querySelector(E('cart-drawer-close-button') + ' svg');
    document.querySelectorAll(MODAL).forEach(modal => {
      flag(modal, 'data-debacq-gift-modal', true);
      if (!source) return;
      const header = modal.querySelector(E('choose-gift-header'));
      const button = header?.querySelector('button, [role="button"]');
      if (!button) return;
      const style = getComputedStyle(source);
      const signature = source.outerHTML + style.width + style.height + style.transform;
      let holder = button.querySelector('[data-debacq-close-icon]');
      if (holder && copiedIcons.get(button) === signature) return;
      const svg = cloneVisual(source, false);
      ['width', 'height', 'transform', 'transform-origin', 'overflow']
        .forEach(property => svg.style.setProperty(
          property, style.getPropertyValue(property), 'important'
        ));
      if (!holder) {
        holder = document.createElement('span');
        holder.setAttribute('data-debacq-close-icon', 'true');
        holder.setAttribute('aria-hidden', 'true');
        button.appendChild(holder);
      }
      holder.replaceChildren(svg);
      flag(button, 'data-debacq-close-ready', true);
      copiedIcons.set(button, signature);
    });
  }

  function rememberNativeLoader() {
    // Reuse an observed native upsell spinner when available.
    // Before one has been observed, the CSS provides a small white spinner.
    for (const button of document.querySelectorAll(E('upsell-add-to-cart-button'))) {
      for (const node of button.querySelectorAll('svg, span, div')) {
        if (!visible(node) || text(node.textContent)) continue;
        const animated = getComputedStyle(node).animationName !== 'none' ||
          !!node.querySelector('animateTransform');
        if (!animated) continue;
        const signature = node.outerHTML;
        if (signature !== loaderSignature) {
          loaderTemplate = cloneVisual(node, true);
          loaderSignature = signature;
        }
        return;
      }
    }
  }

  function applyLoading(row) {
    flag(row, 'data-debacq-loading', true);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!loaderTemplate || row.querySelector('[data-debacq-loader-copy]')) return;
    const holder = document.createElement('span');
    holder.setAttribute('data-debacq-loader-copy', 'true');
    holder.setAttribute('aria-hidden', 'true');
    holder.appendChild(loaderTemplate.cloneNode(true));
    row.appendChild(holder);
    flag(row, 'data-debacq-native-loader', true);
  }

  function clearLoading(message) {
    clearTimeout(pendingTimeout); pendingTimeout = 0;
    document.querySelectorAll('[data-debacq-loading], [data-debacq-native-loader]')
      .forEach(row => {
        flag(row, 'data-debacq-loading', false);
        flag(row, 'data-debacq-native-loader', false);
        row.querySelectorAll('[data-debacq-loader-copy]').forEach(node => node.remove());
      });
    pending = null;
    announce(message || '');
    checkThemeLoading();
  }

  function alertText(modal) {
    return [...modal.querySelectorAll('[role="alert"]')]
      .filter(visible).map(node => text(node.textContent)).join(' ');
  }

  function refreshLoading(modal, cart) {
    if (!pending) return;
    if (!modal) {
      clearLoading();
      return;
    }
    const selected = cart.entries.filter(item => item.zero && item.title === pending.title)
      .reduce((sum, item) => sum + item.quantity, 0);
    if (selected > pending.before) {
      clearLoading('Gift added to your cart.');
      return;
    }
    const error = alertText(modal);
    if (error && error !== pending.previousAlert) {
      clearLoading(); // Keep the app's own error message and retry action.
      return;
    }
    if (Date.now() - pending.started > LOADING_TIMEOUT) {
      clearLoading('The gift is taking longer to add. Check your cart before trying again.');
      return;
    }
    // Recover the loading visual if the app re-renders the selected row.
    modal.querySelectorAll(ROW).forEach(row => {
      if (text(row.querySelector(E('line-item-title'))?.textContent) === pending.title) {
        applyLoading(row);
      }
    });
  }

  function scan() {
    frame = 0;
    if (stopped || !document.body) return;
    markPrices();
    copyCloseIcons();
    rememberNativeLoader();
    const modal = openModal();
    const cart = cartState();
    const chooser = chooseControl();
    refreshLoading(modal, cart);
    checkThemeLoading();

    if (cart.signature !== previousCart) {
      previousCart = cart.signature;
      attempts = 0;
      retryAfter = 0;
    }
    if (modal || cart.free > 0) {
      confirmShown();
      cancelOpen();
      cancelReset();
      return;
    }
    const below = drawerOpen() && cart.ready && cart.paid < BUY_QUANTITY && !chooser;
    if (below && !pending) {
      cancelOpen();
      if (!resetTimer) resetTimer = setTimeout(() => {
        resetTimer = 0;
        markPrices();
        const latest = cartState();
        if (drawerOpen() && latest.ready && latest.paid < BUY_QUANTITY &&
            !openModal() && !chooseControl() && !pending) {
          confirmed = false;
          attempts = 0;
          retryAfter = 0;
          try { sessionStorage.removeItem(STORAGE); } catch (_) {}
        }
      }, RESET_DELAY);
      return;
    }
    cancelReset();
    if (confirmed || pending || document.hidden || !drawerOpen() || !chooser) {
      cancelOpen();
      return;
    }
    if (openTimer || attempts >= 3 || Date.now() < retryAfter) return;
    openTimer = setTimeout(() => {
      openTimer = 0;
      const target = chooseControl();
      if (stopped || confirmed || pending || document.hidden ||
          !drawerOpen() || openModal() || !target) return;
      attempts += 1;
      retryAfter = Date.now() + RETRY_DELAY;
      autoClick = true;
      try { target.click(); }
      finally { autoClick = false; }
      // Confirm only once visible; one bounded retry replaces constant polling.
      clearTimeout(retryTimer); retryTimer = setTimeout(schedule, RETRY_DELAY + 20);
      schedule();
    }, OPEN_DELAY);
  }

  function schedule() {
    if (!stopped && !frame) frame = requestAnimationFrame(scan);
  }

  function onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('#essential-cart-drawer-portal,' + MODAL)) return;
    const action = target.closest([E('quantity-decrement'), E('quantity-increment'),
      E('line-item-remove'), E('upsell-add-to-cart-button'), E('discount-code-button')].join(','));
    if (action?.closest('#essential-cart-drawer-portal') && available(action)) startThemeLoading();
    const row = target.closest(ROW);
    const modal = row?.closest(MODAL);
    if (row && modal && !target.closest('select, option, input, label, a')) {
      if (pending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!available(row)) return;
      const title = text(row.querySelector(E('line-item-title'))?.textContent);
      const cart = cartState();
      pending = {
        title,
        before: cart.entries.filter(item => item.zero && item.title === title)
          .reduce((sum, item) => sum + item.quantity, 0),
        started: Date.now(),
        previousAlert: alertText(modal)
      };
      applyLoading(row);
      startThemeLoading();
      clearTimeout(pendingTimeout); pendingTimeout = setTimeout(schedule, LOADING_TIMEOUT + 20);
      announce('Adding your selected gift.');
      // Allow this first click to continue to the app. No duplicate cart request.
    } else if (!autoClick) {
      const chooser = chooseControl();
      if (chooser && (chooser === target || chooser.contains(target))) {
        cancelOpen();
        attempts = 0;
        retryAfter = Date.now() + RETRY_DELAY;
      }
    }
    schedule();
  }

  function onKey(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.matches(ROW + '[role="button"]')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    // The app keeps its original keyboard activation handler.
  }

  // The app keeps all cart requests. Forward only its user actions to the native
  // top loading bar; no global fetch interception and no cart mutation here.
  function emitLoading(name) {
    document.documentElement.dispatchEvent(new CustomEvent(name, {bubbles:true, detail:{source:'debacq-essential'}}));
  }
  function stopThemeLoading() {
    clearTimeout(loadingTimer); clearTimeout(noBusyTimer);
    loadingTimer = 0; noBusyTimer = 0;
    if (!themeLoading) return;
    themeLoading = false; sawThemeBusy = false;
    submitObserver?.disconnect(); submittedForm = null;
    emitLoading('theme:loading:end');
  }
  function checkThemeLoading() {
    if (!themeLoading) return;
    const checkout = document.querySelector(E('cart-drawer-checkout-button'));
    const busy = checkout?.matches(':disabled, [aria-disabled="true"], [aria-busy="true"]') || submittedForm?.querySelector('[aria-busy="true"]');
    if (busy || pending) sawThemeBusy = true;
    else if (sawThemeBusy) stopThemeLoading();
  }
  function startThemeLoading(form = null) {
    // Do not compete with an older separately installed loading-bridge snippet.
    if (window.__debacqEssentialSignatureLoadingBridge || themeLoading) return;
    themeLoading = true;
    submittedForm = form;
    if (form) {
      submitObserver ||= new MutationObserver(checkThemeLoading);
      submitObserver.observe(form,{subtree:true,attributes:true,attributeFilter:['aria-busy','disabled']});
    }
    sawThemeBusy = false;
    emitLoading('theme:loading:start');
    checkThemeLoading();
    noBusyTimer = setTimeout(() => { if (!sawThemeBusy) stopThemeLoading(); }, 1200);
    loadingTimer = setTimeout(stopThemeLoading, 12000);
  }
  function releaseLoadingLayer() {
    clearTimeout(loadingLayerTimer); loadingLayerTimer = 0;
    const layer = loadingLayer; loadingLayer = null;
    if (layer) {
      const {bar, popover, parent, next, moved} = layer;
      // Cancel only this element's abandoned animation on the safety path. Normal
      // end/fade already finished before this function is scheduled.
      if (layer.timedOut) {
        for (const animation of bar.getAnimations()) animation.cancel();
        bar.style.opacity = '0';
        bar.style.transform = 'scaleX(0)';
      }
      try { if (bar.matches(':popover-open')) bar.hidePopover(); } catch (_) {}
      if (popover === null) bar.removeAttribute('popover');
      else bar.setAttribute('popover',popover);
      if (moved && parent?.isConnected && bar.parentNode === document.body) {
        parent.insertBefore(bar,next?.parentNode === parent ? next : null);
      }
    }
    document.documentElement.removeAttribute('data-debacq-cart-loading');
  }
  function onLoadingStart() {
    nativeLoadingSequence += 1;
    clearTimeout(loadingLayerTimer);
    document.documentElement.setAttribute('data-debacq-cart-loading','');
    const bar = document.querySelector('body > loading-bar.loading-bar');
    if (bar && !loadingLayer) {
      loadingLayer = {bar,popover:bar.getAttribute('popover'),parent:bar.parentNode,next:bar.nextSibling,moved:false};
      let onTop = false;
      if (typeof bar.showPopover === 'function') {
        try {
          bar.setAttribute('popover','manual');
          bar.showPopover(); onTop = true;
        } catch (_) { bar.removeAttribute('popover'); }
      }
      // Older browsers retain the original fixed-position bar, last in paint order.
      if (!onTop) { document.body.append(bar); loadingLayer.moved = true; }
    }
    loadingLayerTimer = setTimeout(() => {
      if (loadingLayer) loadingLayer.timedOut = true;
      releaseLoadingLayer();
    },15000);
  }
  function onLoadingEnd() {
    clearTimeout(loadingLayerTimer);
    // Native completion: 250ms to full width, then a 250ms fade. Keep its layer
    // until both finish. A new start cancels this cleanup rather than hiding it.
    loadingLayerTimer = setTimeout(releaseLoadingLayer,600);
  }
  function onProductSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add') ||
        !form.checkValidity() || event.submitter?.getAttribute('aria-busy') === 'true') return;
    const sequence = nativeLoadingSequence;
    setTimeout(() => {
      // Wait for the complete submit event, including native target handlers.
      // Signature normally emits its own start synchronously. Only fill the gap
      // when another app takes over a valid product-form submission.
      if (!stopped && nativeLoadingSequence === sequence && !themeLoading) startThemeLoading(form);
    },0);
  }
  function onCartSettled() { if (themeLoading) { sawThemeBusy = true; checkThemeLoading(); } }
  function onChange(event) {
    const node = event.target;
    if (!(node instanceof Element) || !node.closest('#essential-cart-drawer-portal')) return;
    if (node.matches('select, input') && node.closest(E('line-item-variant-selector') + ',' + E('quantity-select'))) {
      startThemeLoading(); schedule();
    }
  }
  function onTransition(event) {
    if (event.target instanceof Element && event.target.closest('#essential-cart-drawer-portal,' + MODAL)) schedule();
  }
  const observed = new Set();
  const observer = new MutationObserver(schedule);
  function bindSurfaces() {
    const roots = [...document.querySelectorAll('#essential-cart-drawer-portal,' + MODAL)]
      .filter(node => !node.parentElement?.closest('#essential-cart-drawer-portal,' + MODAL));
    if (roots.length === observed.size && roots.every(node=>observed.has(node))) return;
    observer.disconnect(); observed.clear();
    roots.forEach(node => {
      observed.add(node);
      observer.observe(node, {childList:true, subtree:true, characterData:true, attributes:true,
        attributeFilter:['class','style','hidden','inert','disabled','aria-hidden','aria-disabled','aria-busy','aria-expanded','data-state']});
    });
    schedule();
  }
  // Only discover insertion/removal outside the app. Expensive attribute/text
  // observations stay on Essential surfaces, including a portalled gift dialog.
  const discovery = new MutationObserver(records => {
    if ([...observed].some(node=>!node.isConnected) || records.some(record =>
      [...record.addedNodes].some(node => node instanceof Element &&
        (node.matches('#essential-cart-drawer-portal,'+MODAL) || node.querySelector('#essential-cart-drawer-portal,'+MODAL))))) bindSurfaces();
  });
  function start() {
    if (stopped) return;
    discovery.observe(document.documentElement, {childList:true, subtree:true});
    bindSurfaces();
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('submit', onProductSubmit, true);
    document.addEventListener('theme:loading:start', onLoadingStart);
    document.addEventListener('theme:loading:end', onLoadingEnd);
    document.addEventListener('cart:change', onCartSettled);
    document.addEventListener('cart:error', stopThemeLoading);
    document.addEventListener('transitionend', onTransition, true);
    document.addEventListener('visibilitychange', schedule);
    window.addEventListener('resize', schedule, {passive:true});
    window.addEventListener('pageshow', schedule);
    schedule();
  }

  window[KEY] = {
    version: 9,
    refresh: schedule,
    destroy() {
      stopped = true;
      observer.disconnect(); discovery.disconnect(); observed.clear();
      stopThemeLoading();
      cancelOpen();
      cancelReset();
      clearTimeout(pendingTimeout); clearTimeout(retryTimer);
      cancelAnimationFrame(frame);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('submit', onProductSubmit, true);
      document.removeEventListener('theme:loading:start', onLoadingStart);
      document.removeEventListener('theme:loading:end', onLoadingEnd);
      document.removeEventListener('cart:change', onCartSettled);
      document.removeEventListener('cart:error', stopThemeLoading);
      if (loadingLayer) loadingLayer.timedOut = true;
      releaseLoadingLayer(); submitObserver?.disconnect();
      document.removeEventListener('transitionend', onTransition, true);
      document.removeEventListener('visibilitychange', schedule);
      document.removeEventListener('DOMContentLoaded', start);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('pageshow', schedule);
      clearLoading();
      if (status) status.remove();
      delete window[KEY];
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else start();
})();
